import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/16/solid";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import Image from "../Image/Image";
import { getNestedValue } from "@repo/utilities/object.utility.ts";

interface IMultiSearchSelectorV1Props {
  label: string;
  className?: string;
  optionsClassName?: string;
  disabled?: boolean;
  placeholder: string;
  accessorKey?: string;
  imageAccessorKey?: string;
  emptyText?: string;
  anchorPosition?: "top" | "bottom" | "left" | "right";
  error?: any;
  validateQuery?: (event: ChangeEvent<HTMLInputElement>) => void;
  selectedValues: Record<any, any>[];
  options: any;
  onChange: (option: Record<string, any> | null) => void;
  by?: string;
}

function MultiSearchSelectorV1({
  label,
  className,
  options,
  optionsClassName,
  disabled = false,
  selectedValues = [],
  placeholder,
  accessorKey = "name",
  imageAccessorKey = "logo",
  onChange,
  emptyText = "No Data Found!",
  error,
  anchorPosition = "bottom",
  by = "id",
}: IMultiSearchSelectorV1Props) {
  const [isOpen, setIsOpen] = useState(false);
  const prevOpen = useRef(false);
  useEffect(() => {
    prevOpen.current = isOpen;
  }, [isOpen]);
  return (
    <Listbox
      value={Array.isArray(selectedValues) ? selectedValues : []}
      onChange={onChange}
      multiple
      by={by}
    >
      {({ open }) => {
        if (open !== isOpen) setIsOpen(open);
        return (
          <>
            <ListboxButton
              className={`relative min-w-full rounded-lg outline-none transition-colors   
                  disabled:cursor-not-allowed disabled:opacity-70 disabled:bg-extraLightGray/30 
                  ${className}`}
              disabled={disabled}
            >
              <div className="flex items-center justify-between gap-2">
                {selectedValues?.length > 0 ? (
                  <>
                    <span className="text-sm text-subHeading font-semibold">
                      {selectedValues?.length} Selected
                    </span>
                  </>
                ) : (
                  <span className="text-sm text-darkGray/80  font-medium">
                    {placeholder}
                  </span>
                )}
                <ChevronUpDownIcon
                  className="size-5 text-gray"
                  aria-hidden="true"
                />
              </div>
            </ListboxButton>
            {error?.message && (
              <p className="text-xs text-error font-medium pl-2 mt-0.5">
                {error?.message}
              </p>
            )}

            {open ? (
              <ListboxOptions
                as="div"
                anchor={anchorPosition}
                className="[--anchor-gap:4px] relative shadow-md border bg-white border-extraLightGray flex flex-col rounded select-none outline-none z-50 p-2"
              >
                {label && (
                  <label className="text-sm text-body flex items-center text-left font-semibold p-1.5 border-b border-lightGray">
                    {label}
                  </label>
                )}
                <div
                  className={`py-1 flex flex-col max-h-80 overflow-y-auto ${optionsClassName} `}
                >
                  {options?.length ? (
                    options?.map((optionValue: any) => {
                      return (
                        <ListboxOption
                          value={optionValue}
                          key={optionValue?.id}
                          className="flex cursor-pointer hover:bg-primary/20 items-center w-full text-sm font-medium p-2 gap-2 border-b border-lightGray"
                        >
                          {({ selected }) => (
                            <>
                              <span
                                data-selected={selected}
                                className="rounded h-4 w-4 p-0.5 flex items-center justify-center ring-1 ring-extraLightGray data-[selected=true]:bg-primary data-[selected=true]:text-white data-[selected=true]:ring-primary bg-white"
                              >
                                {selected && (
                                  <CheckIcon className="size-4 stroke-[3] text-inherit" />
                                )}
                              </span>
                              <Image
                                src={String(
                                  getNestedValue(optionValue, imageAccessorKey)
                                )}
                                className="h-6 w-10 bg-lightGray/30 p-1 rounded-md ring-1 ring-offWhite"
                              />
                              <span className={`line-clamp-1 ml-2 `}>
                                {String(
                                  getNestedValue(optionValue, accessorKey)
                                )}
                              </span>
                            </>
                          )}
                        </ListboxOption>
                      );
                    })
                  ) : (
                    <p className="text-sm px-2 font-medium text-darkGray text-center">
                      {emptyText}
                    </p>
                  )}
                </div>
              </ListboxOptions>
            ) : null}
          </>
        );
      }}
    </Listbox>
  );
}

export default MultiSearchSelectorV1;
